import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import axios from 'axios';
import './App.css';

interface FormState {
  name: string;
  email: string;
  phone: string;
  message: string;
}

type Status = 'idle' | 'loading' | 'success' | 'error';
type Touched = Partial<Record<keyof FormState, boolean>>;

const domain = window.location.hostname || 'yourdomain.com';
const apiBase = process.env.REACT_APP_API_URL || '';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+\d][\d\s\-().]{6,}$/;

function validate(form: FormState) {
  const errors: Partial<Record<keyof FormState, string>> = {};
  if (!form.name.trim())               errors.name  = 'Name is required.';
  if (!form.email.trim())              errors.email = 'Email is required.';
  else if (!EMAIL_RE.test(form.email)) errors.email = 'Enter a valid email address.';
  if (form.phone && !PHONE_RE.test(form.phone)) errors.phone = 'Enter a valid phone number.';
  return errors;
}

const App: React.FC = () => {
  const [form, setForm]         = useState<FormState>({ name: '', email: '', phone: '', message: '' });
  const [touched, setTouched]   = useState<Touched>({});
  const [status, setStatus]     = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const errors  = validate(form);
  const isValid = Object.keys(errors).length === 0 && (!RECAPTCHA_SITE_KEY || !!captchaToken);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      await axios.post(`${apiBase}/api/inquiry`, { ...form, domain, captchaToken });
      setStatus('success');
    } catch (err) {
      const msg = axios.isAxiosError(err)
        ? err.response?.data?.error || 'Something went wrong.'
        : 'Unexpected error.';
      setErrorMsg(msg);
      setStatus('error');
      // Reset captcha so the user can retry
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  const fieldProps = (name: keyof FormState) => ({
    name,
    value: form[name],
    onChange: handleChange,
    onBlur: handleBlur,
    disabled: status === 'loading',
    className: touched[name] && errors[name] ? 'input-error' : '',
  });

  return (
    <div className="page">
      <div className="card">
        <h1 className="headline"><span className="headline-domain">{domain}</span> is for sale.</h1>
        <p className="subline">
          Interested in acquiring <strong>{domain}</strong>? Drop your details below
          and we&apos;ll be in touch.
        </p>

        {status === 'success' ? (
          <div className="success-box">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            <p>Got it! We&apos;ll reach out to you shortly.</p>
          </div>
        ) : (
          <form className="form" onSubmit={handleSubmit} noValidate>
            <div className="field-row">
              <div className="field">
                <label htmlFor="name">Name <span className="required">*</span></label>
                <input id="name" type="text" placeholder="Jane Smith" {...fieldProps('name')} />
                {touched.name && errors.name && <span className="field-error">{errors.name}</span>}
              </div>
              <div className="field">
                <label htmlFor="email">Email <span className="required">*</span></label>
                <input id="email" type="email" placeholder="jane@example.com" {...fieldProps('email')} />
                {touched.email && errors.email && <span className="field-error">{errors.email}</span>}
              </div>
            </div>

            <div className="field">
              <label htmlFor="phone">Phone <span className="optional">(optional)</span></label>
              <input id="phone" type="tel" placeholder="+1 555 000 0000" {...fieldProps('phone')} />
              {touched.phone && errors.phone && <span className="field-error">{errors.phone}</span>}
            </div>

            <div className="field">
              <label htmlFor="message">Message <span className="optional">(optional)</span></label>
              <textarea id="message" rows={4} placeholder="Tell us your offer or any questions…" {...fieldProps('message')} />
            </div>

            {RECAPTCHA_SITE_KEY ? (
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={token => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />
            ) : (
              <p className="field-error">⚠ REACT_APP_RECAPTCHA_SITE_KEY is not set.</p>
            )}

            {status === 'error' && (
              <div className="error-box">{errorMsg}</div>
            )}

            <button type="submit" className="submit-btn" disabled={!isValid || status === 'loading'}>
              {status === 'loading' ? <span className="spinner" /> : 'Send Inquiry'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default App;
